import { useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Button,
    Menu,
    MenuItem,
    TextField,
    Tabs,
    Tab,
    Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ITripTraveller, ICountryData } from "@interfaces";
import { useAppDispatch } from "src/Redux/store";
import { setRosterTravellers } from "src/Redux/Slices/tripPlanner";
import { externalApi } from "src/Api/externalApi";
import { cartApi } from "src/Api/cartApi";
import { extractGuests } from "src/Utilities/addressBook";
import {
    guestToTraveller,
    emptyTraveller,
    ensureRosterCount,
    isTravellerComplete,
    newTravellerId,
    travellerIdentityKey,
} from "src/Utilities/tripPlannerHelpers";

interface Props {
    open: boolean;
    /** Current roster from Redux. */
    travellers: ITripTraveller[];
    /** Trip-level people count — used to seed blank rows when the roster is empty. */
    numTravellers: number;
    /**
     * When true, fetch the account-level saved travellers (people the user has
     * entered on any previous trip/quote) and let them be imported into the
     * roster instead of re-typing. Only meaningful for authenticated users.
     */
    canUseSavedTravellers?: boolean;
    onClose: () => void;
}

const TITLE_OPTIONS = ["Mr", "Mrs", "Miss", "Ms", "Master", "Dr"];

/**
 * Trip-level traveller ROSTER editor. The user defines each person once here
 * (opened from the "Travellers" button in the trip header). People are then
 * referenced per-product via the traveller picker, so details are never
 * re-typed. Product-specific optional fields are NOT edited here — they belong
 * to the product (see TravellerPickerModal).
 *
 * Works on a local copy and only commits to Redux on Save so Cancel discards.
 */
export const RosterModal = ({
    open,
    travellers,
    numTravellers,
    canUseSavedTravellers = false,
    onClose,
}: Props) => {
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState(0);
    const [people, setPeople] = useState<ITripTraveller[]>([]);
    const [countries, setCountries] = useState<ICountryData[]>([]);
    const [savedTravellers, setSavedTravellers] = useState<ITripTraveller[]>([]);
    const [savedMenuAnchor, setSavedMenuAnchor] = useState<null | HTMLElement>(null);

    // Seed the local form when the modal opens. An empty roster is seeded with
    // `numTravellers` blank people so the user has a row per planned traveller.
    useEffect(() => {
        if (!open) return;
        const seeded = travellers.length
            ? travellers.map((t) => ({ ...t }))
            : ensureRosterCount([], Math.max(1, numTravellers));
        setPeople(seeded);
        setActiveTab(0);
        setSavedMenuAnchor(null);
        // Drop any cached list from a previous open so a person saved earlier
        // this session is picked up by the refetch below.
        setSavedTravellers([]);
    }, [open]);

    // Account-level saved travellers — the same "guests" the user manages in the
    // Profile Address Book (read from GET cart/customers). Refetched on every
    // open for authenticated users so people saved elsewhere appear and can be
    // imported instead of re-typed. Non-fatal: on any failure the picker hides.
    useEffect(() => {
        if (!open || !canUseSavedTravellers) return;
        let cancelled = false;
        cartApi
            .getCustomers()
            .then((resp) => {
                if (cancelled) return;
                setSavedTravellers(extractGuests(resp).map(guestToTraveller));
            })
            .catch(() => {
                /* non-fatal — saved-traveller picker stays hidden */
            });
        return () => {
            cancelled = true;
        };
    }, [open, canUseSavedTravellers]);

    // Country list (once) for the Country dropdown.
    useEffect(() => {
        if (!open || countries.length) return;
        let cancelled = false;
        externalApi
            .getCountriesDate()
            .then((resp) => {
                if (!cancelled) setCountries(resp.data ?? []);
            })
            .catch(() => {
                /* non-fatal — Country becomes a free-text fallback */
            });
        return () => {
            cancelled = true;
        };
    }, [open, countries.length]);

    const completedCount = useMemo(
        () => people.filter(isTravellerComplete).length,
        [people],
    );

    const updatePerson = (index: number, patch: Partial<ITripTraveller>) => {
        setPeople((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
    };

    const addPerson = () => {
        setPeople((prev) => {
            const next = [...prev, emptyTraveller()];
            setActiveTab(next.length - 1);
            return next;
        });
    };

    // Saved travellers not already present in the roster (deduped by name + DOB).
    const availableSaved = useMemo(() => {
        const present = new Set(people.map(travellerIdentityKey));
        return savedTravellers.filter((t) => !present.has(travellerIdentityKey(t)));
    }, [savedTravellers, people]);

    // Import a saved traveller: fill the first fully-blank row if one exists
    // (so seeded empty tabs get used up first), otherwise append a new tab.
    const addFromSaved = (t: ITripTraveller) => {
        setSavedMenuAnchor(null);
        setPeople((prev) => {
            const copy = { ...t, id: newTravellerId() };
            const blankIdx = prev.findIndex(
                (p) => !p.firstName?.trim() && !p.lastName?.trim(),
            );
            if (blankIdx >= 0) {
                setActiveTab(blankIdx);
                return prev.map((p, i) => (i === blankIdx ? copy : p));
            }
            setActiveTab(prev.length);
            return [...prev, copy];
        });
    };

    const removePerson = (index: number) => {
        setPeople((prev) => {
            const next = prev.filter((_t, i) => i !== index);
            setActiveTab((tab) => Math.max(0, Math.min(tab, next.length - 1)));
            return next;
        });
    };

    const handleSave = () => {
        // Drop fully-blank rows so an empty trailing tab doesn't become a ghost
        // roster entry; always keep at least one person. `id` is internal and
        // `dateOfBirth` is pre-filled with a default, so neither counts as the
        // user having "touched" the row.
        const cleaned = people.filter(
            (t) =>
                isTravellerComplete(t) ||
                Object.entries(t).some(
                    ([k, v]) =>
                        k !== "id" &&
                        k !== "dateOfBirth" &&
                        typeof v === "string" &&
                        v.trim() !== "",
                ),
        );
        dispatch(setRosterTravellers({ travellers: cleaned }));
        onClose();
    };

    const current = people[activeTab];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 5 }}>
                Travellers
                <Typography variant="body2" color="text.secondary">
                    {completedCount}/{people.length} entered
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", right: 8, top: 8 }}
                    aria-label="Close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Tabs
                        value={Math.min(activeTab, Math.max(0, people.length - 1))}
                        onChange={(_e, v) => setActiveTab(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ flex: 1 }}
                    >
                        {people.map((t, i) => (
                            <Tab
                                key={t.id}
                                iconPosition="end"
                                icon={
                                    isTravellerComplete(t) ? (
                                        <CheckCircleIcon sx={{ fontSize: 16, color: "#43a047" }} />
                                    ) : undefined
                                }
                                label={
                                    isTravellerComplete(t)
                                        ? `${t.firstName} ${t.lastName}`.trim()
                                        : `Traveller ${i + 1}`
                                }
                            />
                        ))}
                    </Tabs>
                    <Button
                        size="small"
                        startIcon={<PersonAddAltIcon />}
                        onClick={addPerson}
                        sx={{ ml: 1, flexShrink: 0 }}
                    >
                        Add
                    </Button>
                    {canUseSavedTravellers && savedTravellers.length > 0 && (
                        <>
                            <Button
                                size="small"
                                startIcon={<PeopleAltOutlinedIcon />}
                                onClick={(e) => setSavedMenuAnchor(e.currentTarget)}
                                sx={{ ml: 1, flexShrink: 0 }}
                            >
                                Saved
                            </Button>
                            <Menu
                                anchorEl={savedMenuAnchor}
                                open={Boolean(savedMenuAnchor)}
                                onClose={() => setSavedMenuAnchor(null)}
                            >
                                {availableSaved.length === 0 ? (
                                    <MenuItem disabled>All saved travellers added</MenuItem>
                                ) : (
                                    availableSaved.map((t) => (
                                        <MenuItem key={t.id} onClick={() => addFromSaved(t)}>
                                            {`${t.firstName} ${t.lastName}`.trim() ||
                                                "Unnamed traveller"}
                                        </MenuItem>
                                    ))
                                )}
                            </Menu>
                        </>
                    )}
                </Box>

                {current && (
                    <Stack spacing={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Title"
                            value={current.title}
                            onChange={(e) => updatePerson(activeTab, { title: e.target.value })}
                        >
                            {TITLE_OPTIONS.map((t) => (
                                <MenuItem key={t} value={t}>
                                    {t}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="First name"
                                required
                                value={current.firstName}
                                onChange={(e) =>
                                    updatePerson(activeTab, { firstName: e.target.value })
                                }
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label="Last name"
                                required
                                value={current.lastName}
                                onChange={(e) =>
                                    updatePerson(activeTab, { lastName: e.target.value })
                                }
                            />
                        </Stack>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Date of birth"
                            InputLabelProps={{ shrink: true }}
                            value={current.dateOfBirth}
                            onChange={(e) =>
                                updatePerson(activeTab, { dateOfBirth: e.target.value })
                            }
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            label="Email"
                            value={current.email}
                            onChange={(e) => updatePerson(activeTab, { email: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Phone number"
                            value={current.phoneNumber}
                            onChange={(e) =>
                                updatePerson(activeTab, { phoneNumber: e.target.value })
                            }
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Postcode"
                                value={current.postalCode}
                                onChange={(e) =>
                                    updatePerson(activeTab, { postalCode: e.target.value })
                                }
                            />
                            {countries.length ? (
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={countries}
                                    autoHighlight
                                    getOptionLabel={(o) => o.name}
                                    isOptionEqualToValue={(o, v) =>
                                        o.countryCode === v.countryCode
                                    }
                                    value={
                                        countries.find(
                                            (c) => c.countryCode === current.countryCode,
                                        ) ?? null
                                    }
                                    onChange={(_e, val) =>
                                        updatePerson(activeTab, {
                                            countryCode: val?.countryCode ?? "",
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Country" size="small" />
                                    )}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Country"
                                    value={current.countryCode}
                                    onChange={(e) =>
                                        updatePerson(activeTab, { countryCode: e.target.value })
                                    }
                                />
                            )}
                        </Stack>

                        <Button
                            color="error"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => removePerson(activeTab)}
                            sx={{ alignSelf: "flex-start" }}
                            disabled={people.length <= 1}
                        >
                            Remove this traveller
                        </Button>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">
                    Save travellers
                </Button>
            </DialogActions>
        </Dialog>
    );
};
